import { useState, useEffect, useMemo, useCallback } from "react";
import { usePersistedState } from "../../hooks/usePersistedState";
import { ContainerCard } from "../../components/ui/cards";
import { cn } from "../../utils/tailwindUtils";
import {
    RefreshCw, Search, ChevronDown, ArrowUpRight, ArrowDownLeft, ChevronLeft, ChevronRight,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { EMAIL_MESSAGES_URL, EMAIL_ANALYTICS_URL, EMAIL_ACCOUNTS_URL, USERS_SIMPLE_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import dayjs from "dayjs";

// ── Date presets ──────────────────────────────────────────────────────────────

const DATE_PRESETS = [
    { value: "all",        label: "All Time" },
    { value: "today",      label: "Today" },
    { value: "yesterday",  label: "Yesterday" },
    { value: "last_week",  label: "Last 7 Days" },
    { value: "last_month", label: "Last 30 Days" },
    { value: "this_month", label: "This Month" },
    { value: "custom",     label: "Custom Range" },
];

const resolveDateRange = (preset, customFrom, customTo) => {
    const today = dayjs();
    const fmt = d => d.format("YYYY-MM-DD");
    switch (preset) {
        case "today":      return { from_date: fmt(today),                         to_date: fmt(today) };
        case "yesterday":  return { from_date: fmt(today.subtract(1, "day")),      to_date: fmt(today.subtract(1, "day")) };
        case "last_week":  return { from_date: fmt(today.subtract(7,  "day")),     to_date: fmt(today) };
        case "last_month": return { from_date: fmt(today.subtract(30, "day")),     to_date: fmt(today) };
        case "this_month": return { from_date: fmt(today.startOf("month")), to_date: fmt(today) };
        case "custom":     return (customFrom && customTo)
            ? { from_date: customFrom, to_date: customTo }
            : {};
        default:           return {};
    }
};

// ── Engagement badges ─────────────────────────────────────────────────────────

const EngageBadge = ({ label, color }) => (
    <span className={cn("px-1.5 py-0.5 text-[9px] font-semibold rounded-full", color)}>{label}</span>
);

// ── Main component ────────────────────────────────────────────────────────────

const EmailAnalyticsReport = () => {
    const { fetch } = useApiMethods();

    const [messages,    setMessages]    = useState([]);
    const [summary,     setSummary]     = useState(null);
    const [loading,     setLoading]     = useState(true);
    const [search,      setSearch]      = useState("");
    const [dirFilter,      setDirFilter]      = useState(null);   // null | 'sent' | 'received'
    const [statusFilter,   setStatusFilter]   = useState(null);  // null | 'opened' | 'clicked' | 'bounced'
    const [users,          setUsers]          = useState([]);
    const [selectedUserId, setSelectedUserId] = usePersistedState("email_report_user", "");
    const [mailboxes,      setMailboxes]      = useState([]);
    const [mailboxId,      setMailboxId]      = usePersistedState("email_report_mailbox", "");
    const [datePreset,     setDatePreset]     = useState("last_month");
    const [customFrom,  setCustomFrom]  = useState("");
    const [customTo,    setCustomTo]    = useState("");
    const [page,        setPage]        = useState(1);
    const [pageSize,    setPageSize]    = useState(25);

    const load = useCallback(() => {
        if (datePreset === "custom" && (!customFrom || !customTo)) return;
        setLoading(true);
        const { from_date, to_date } = resolveDateRange(datePreset, customFrom, customTo);
        const p = new URLSearchParams();
        if (from_date)      p.append("from_date",  from_date);
        if (to_date)        p.append("to_date",    to_date);
        if (selectedUserId) p.append("sent_by_id", selectedUserId);
        if (mailboxId)      p.append("account_id", mailboxId);
        const qs     = p.toString();
        const msgUrl = qs ? `${EMAIL_MESSAGES_URL}?${qs}` : EMAIL_MESSAGES_URL;
        const sumUrl = qs ? `${EMAIL_ANALYTICS_URL}?${qs}` : EMAIL_ANALYTICS_URL;

        Promise.all([fetch(msgUrl), fetch(sumUrl)])
            .then(([msgRes, sumRes]) => {
                if (msgRes?.status === "success") setMessages(msgRes.data || []);
                else ToastMsg("error", msgRes?.message || "Failed to load emails");
                if (sumRes?.status === "success") setSummary(sumRes.data);
            })
            .catch(() => ToastMsg("error", "Failed to load report"))
            .finally(() => setLoading(false));
    }, [datePreset, customFrom, customTo, selectedUserId, mailboxId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        fetch(USERS_SIMPLE_URL).then(res => {
            if (res?.status === "success") setUsers(res.data || []);
        }).catch(() => {});
        fetch(EMAIL_ACCOUNTS_URL).then(res => {
            if (res?.status === "success") setMailboxes(res.data || []);
        }).catch(() => {});
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { setPage(1); }, [dirFilter, statusFilter, search]);

    const filtered = useMemo(() => {
        let rows = messages;
        if (dirFilter)    rows = rows.filter(r => r.direction === dirFilter);
        if (statusFilter === 'opened')  rows = rows.filter(r => r.is_opened);
        if (statusFilter === 'clicked') rows = rows.filter(r => r.is_clicked);
        if (statusFilter === 'bounced') rows = rows.filter(r => r.is_bounced);
        if (statusFilter === 'replied') rows = rows.filter(r => r.is_replied);
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            rows = rows.filter(r =>
                r.subject?.toLowerCase().includes(q) ||
                r.to_email?.toLowerCase().includes(q) ||
                r.from_email?.toLowerCase().includes(q) ||
                r.customer_name?.toLowerCase().includes(q) ||
                r.sent_by_name?.toLowerCase().includes(q)
            );
        }
        return rows;
    }, [messages, dirFilter, statusFilter, search]);

    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const paginated  = filtered.slice((page - 1) * pageSize, page * pageSize);

    const sentCount     = messages.filter(m => m.direction === 'sent').length;
    const receivedCount = messages.filter(m => m.direction === 'received').length;
    const openedCount   = messages.filter(m => m.is_opened).length;
    const clickedCount  = messages.filter(m => m.is_clicked).length;
    const bouncedCount  = messages.filter(m => m.is_bounced).length;
    const repliedCount  = messages.filter(m => m.is_replied).length;
    const byMailbox     = summary?.mailbox_breakdown || [];
    const openRate      = sentCount ? (openedCount / sentCount * 100).toFixed(1) : 0;
    const clickRate     = sentCount ? (clickedCount / sentCount * 100).toFixed(1) : 0;

    return (
        <ContainerCard>
            {/* ── Header ── */}
            <div className="flex items-center justify-between mb-4 px-1">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Email Analytics Report</h1>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Email activity, open/click tracking and deliverability
                    </p>
                </div>
                <Button onClick={load} size="sm" variant="outline" className="flex items-center gap-1 text-xs" disabled={loading}>
                    <RefreshCw className={cn("h-3 w-3", loading && "animate-spin")} />
                    Refresh
                </Button>
            </div>

            {/* ── Date filter ── */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="relative">
                    <select
                        value={datePreset}
                        onChange={e => {
                            setDatePreset(e.target.value);
                            if (e.target.value !== "custom") { setCustomFrom(""); setCustomTo(""); }
                        }}
                        className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer"
                    >
                        {DATE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                </div>
                {datePreset === "custom" && (
                    <>
                        <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                            className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400" />
                        <span className="text-xs text-gray-400">to</span>
                        <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                            className="text-xs border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400" />
                        <Button onClick={() => load()} size="sm" variant="outline" className="text-xs px-3" disabled={loading}>Apply</Button>
                    </>
                )}
            </div>

            {/* ── Filter badges ── */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                {/* Direction filter */}
                {[
                    { value: null,       label: `All · ${messages.length}` },
                    { value: 'sent',     label: `Sent · ${sentCount}` },
                    { value: 'received', label: `Received · ${receivedCount}` },
                ].map(opt => (
                    <button key={String(opt.value)} onClick={() => setDirFilter(opt.value)}
                        className={cn(
                            "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                            dirFilter === opt.value
                                ? "bg-gray-700 text-white border-gray-800 dark:bg-gray-200 dark:text-gray-900"
                                : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-200 dark:hover:bg-gray-700"
                        )}
                    >{opt.label}</button>
                ))}

                <div className="w-px h-4 bg-gray-200 dark:bg-gray-700 mx-1" />

                {/* Status filter */}
                {[
                    { value: 'opened',  label: `Opened · ${openedCount}`,  color: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-300 dark:border-green-800' },
                    { value: 'clicked', label: `Clicked · ${clickedCount}`, color: 'bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-300 dark:border-purple-800' },
                    { value: 'replied', label: `Replied · ${repliedCount}`, color: 'bg-sky-100 text-sky-700 border-sky-200 dark:bg-sky-900/20 dark:text-sky-300 dark:border-sky-800' },
                    { value: 'bounced', label: `Bounced · ${bouncedCount}`, color: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-300 dark:border-red-800' },
                ].map(opt => (
                    <button key={opt.value} onClick={() => setStatusFilter(statusFilter === opt.value ? null : opt.value)}
                        className={cn(
                            "px-3 py-1 rounded-full text-xs font-medium border transition-colors",
                            statusFilter === opt.value
                                ? "bg-indigo-600 text-white border-indigo-700"
                                : cn(opt.color, "hover:opacity-90")
                        )}
                    >{opt.label}</button>
                ))}

                {/* Rep filter */}
                <div className="relative">
                    <select value={selectedUserId} onChange={e => setSelectedUserId(e.target.value)}
                        className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer"
                    >
                        <option value="">All Reps</option>
                        {users.map(u => {
                            const name = [u.login_fname, u.login_lname].filter(Boolean).join(" ") || u.login_name;
                            return <option key={u.id} value={u.id}>{name}</option>;
                        })}
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                </div>
                {/* Mailbox filter — received mail and replies only exist for a
                    mailbox once it has been synced (Email Integration → Sync now). */}
                {mailboxes.length > 0 && (
                    <div className="relative">
                        <select value={mailboxId} onChange={e => setMailboxId(e.target.value)}
                            className="appearance-none pl-3 pr-8 py-1.5 text-xs border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none focus:ring-1 focus:ring-indigo-400 cursor-pointer"
                        >
                            <option value="">All Mailboxes</option>
                            {mailboxes.map(m => <option key={m.id} value={m.id}>{m.email_address}</option>)}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    </div>
                )}
                {/* Search */}
                <div className="ml-auto flex items-center gap-1.5 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1 bg-white dark:bg-gray-800">
                    <Search className="h-3 w-3 text-gray-400" />
                    <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                        placeholder="Subject, customer, rep…"
                        className="text-xs bg-transparent outline-none text-gray-700 dark:text-gray-300 w-40" />
                </div>
            </div>

            {/* ── By mailbox ── */}
            {!loading && byMailbox.length > 0 && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden mb-4">
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700/50 text-xs font-semibold text-gray-700 dark:text-gray-200">
                        By mailbox
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-left border-b border-gray-100 dark:border-gray-700">
                                    {["Mailbox", "Sent", "Received", "Opened", "Replied", "Reply rate", "Leads", "Last synced"].map(h => (
                                        <th key={h} className="px-3 py-2 font-semibold text-gray-600 dark:text-gray-300">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {byMailbox.map(r => (
                                    <tr key={r.account_id} className="text-gray-700 dark:text-gray-300">
                                        <td className="px-3 py-2">
                                            <p className="font-medium text-gray-800 dark:text-gray-100">{r.email_address}</p>
                                            <p className="text-[10px] text-gray-400">{r.provider_label}</p>
                                        </td>
                                        <td className="px-3 py-2">{r.sent}</td>
                                        <td className="px-3 py-2">{r.received}</td>
                                        <td className="px-3 py-2">{r.opened}</td>
                                        <td className="px-3 py-2">{r.replied}</td>
                                        <td className="px-3 py-2">{r.reply_rate}%</td>
                                        <td className="px-3 py-2">{r.leads}</td>
                                        <td className="px-3 py-2 text-gray-400">
                                            {r.last_synced_at ? dayjs(r.last_synced_at).format("DD MMM, hh:mm A") : "Never"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* ── Table ── */}
            {loading ? (
                <div className="flex items-center justify-center h-48">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto" />
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading report…</p>
                    </div>
                </div>
            ) : (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="bg-gray-50 dark:bg-gray-700/50 text-left">
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300 w-8">#</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Date</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Direction</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Subject</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">To / From</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Sent By</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Template</th>
                                    <th className="px-3 py-2.5 font-semibold text-gray-600 dark:text-gray-300">Engagement</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="text-center py-10 text-xs text-gray-400 dark:text-gray-500">
                                            No emails found
                                        </td>
                                    </tr>
                                ) : paginated.map((row, idx) => (
                                    <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                                        <td className="px-3 py-2.5 text-gray-400 dark:text-gray-500">{(page - 1) * pageSize + idx + 1}</td>
                                        <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                            {row.sent_at ? dayjs(row.sent_at).format("DD MMM YYYY HH:mm") : "—"}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {row.direction === 'sent' ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                                    <ArrowUpRight className="h-2.5 w-2.5" /> Sent
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                                                    <ArrowDownLeft className="h-2.5 w-2.5" /> Received
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5 font-medium text-gray-800 dark:text-gray-200 max-w-[200px] truncate">
                                            {row.subject || "—"}
                                        </td>
                                        <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 max-w-[160px] truncate">
                                            {row.direction === 'sent' ? row.to_email : row.from_email}
                                        </td>
                                        <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                            {row.sent_by_name || "—"}
                                        </td>
                                        <td className="px-3 py-2.5 text-gray-500 dark:text-gray-400 max-w-[120px] truncate">
                                            {row.template_name || <span className="text-gray-300 dark:text-gray-600">—</span>}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <div className="flex items-center gap-1 flex-wrap">
                                                {row.is_opened && (
                                                    <EngageBadge
                                                        label={`Opened${row.open_count > 1 ? ` ×${row.open_count}` : ''}`}
                                                        color="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                                    />
                                                )}
                                                {row.is_clicked && (
                                                    <EngageBadge
                                                        label={`Clicked${row.click_count > 1 ? ` ×${row.click_count}` : ''}`}
                                                        color="bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                                                    />
                                                )}
                                                {row.is_replied && (
                                                    <EngageBadge label="Replied" color="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" />
                                                )}
                                                {row.is_bounced && (
                                                    <EngageBadge label="Bounced" color="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400" />
                                                )}
                                                {!row.is_opened && !row.is_clicked && !row.is_replied && !row.is_bounced && (
                                                    <span className="text-gray-300 dark:text-gray-600">—</span>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="sticky bottom-0 px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-gray-500 dark:text-gray-400">Rows per page</span>
                            <select
                                value={pageSize}
                                onChange={e => setPageSize(Number(e.target.value))}
                                className="text-[10px] border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 outline-none"
                            >
                                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                {filtered.length === 0 ? '0' : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, filtered.length)}`} of {filtered.length}
                            </span>
                            <span className="text-[10px] text-gray-400 dark:text-gray-500 ml-2">
                                Open rate {openRate}% · Click rate {clickRate}%
                            </span>
                        </div>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setPage(1)} disabled={page === 1} className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">«</button>
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronLeft className="h-3 w-3" /></button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                                .reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i-1] > 1) acc.push('…'); acc.push(p); return acc; }, [])
                                .map((p, i) => p === '…'
                                    ? <span key={`e${i}`} className="px-1 text-[10px] text-gray-400">…</span>
                                    : <button key={p} onClick={() => setPage(p)} className={cn("min-w-[22px] px-1.5 py-0.5 text-[10px] rounded border transition-colors", p === page ? "bg-indigo-600 text-white border-indigo-600" : "border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700")}>{p}</button>
                                )
                            }
                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-0.5 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"><ChevronRight className="h-3 w-3" /></button>
                            <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="px-1.5 py-0.5 text-[10px] rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300">»</button>
                        </div>
                    </div>
                </div>
            )}
        </ContainerCard>
    );
};

export default EmailAnalyticsReport;
